import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterialColor, useMaterials } from "hooks/useMaterials";
import { useMemo } from "react";
import { RailcarSlotMaterialSpan } from "store/api/generatedApi";

/** The material expected in each area at `timestamp`. */
export const useReportedRailcarMaterials = (
  spans: RailcarSlotMaterialSpan[] | undefined,
  timestamp: Dayjs
): Record<string, string> =>
  useMemo(
    () =>
      Object.fromEntries(
        (spans ?? [])
          .filter(
            (span) =>
              !timestamp.isBefore(span.start) && timestamp.isBefore(span.end)
          )
          .map((span): [string, string] => [span.area, span.material])
      ),
    [spans, timestamp]
  );

/**
 * Area labels for the map: the labeller's own mapping, with the reported
 * materials filling the areas they have not set yet.
 *
 * Only the reported ones are suffixed. The colours are identical either way, so
 * without it a labeller cannot tell their own judgement from the plant's.
 */
export const useAreaMaterialLabels = (
  currentMapping: Record<string, string>,
  reportedMaterials: Record<string, string>
): Record<string, string> => {
  const { materials } = useMaterials();
  const { t } = useTenantTranslation();
  return useMemo(() => {
    const labellingName = (materialName: string) =>
      materials?.find(({ name }) => name === materialName)?.labelling_name ??
      materialName;
    return Object.fromEntries([
      ...Object.entries(reportedMaterials)
        .filter(([area]) => currentMapping[area] === undefined)
        .map(([area, materialName]): [string, string] => [
          area,
          t("reportedMaterial", { material: labellingName(materialName) }),
        ]),
      ...Object.entries(currentMapping).map(
        ([area, materialName]): [string, string] => [
          area,
          labellingName(materialName),
        ]
      ),
    ]);
  }, [currentMapping, reportedMaterials, materials, t]);
};

/**
 * Area colours for the map: the labeller's own mapping, with the expected
 * materials filling the areas they have not set yet.
 */
export const useAreaMaterialColors = (
  currentMapping: Record<string, string>,
  reportedMaterials: Record<string, string>
): Record<string, string> => {
  const { getColor } = useMaterialColor();
  return useMemo(
    () =>
      Object.fromEntries(
        Object.entries({ ...reportedMaterials, ...currentMapping })
          .map(([area, material]): [string, string] => [
            area,
            getColor(material),
          ])
          .filter(([, color]) => color)
      ),
    [currentMapping, reportedMaterials, getColor]
  );
};
